// Persistent client-side cache for the family chat thread.
//
// Why a dedicated module
// ----------------------
// The chat surface renders before any RPC has returned so the user sees
// their history immediately. On every mount/realtime-tick we still want
// to ask the backend "is anything new?" via `chat_snapshot_if_changed`.
// To keep the in-memory rows and the on-disk cache in lockstep, this
// module owns:
//
//   * The `localStorage` key shape (versioned, isolated by user + thread)
//   * The on-disk payload schema (also versioned, so a future schema
//     bump is a clean cut rather than a silent misread)
//   * The defensive read/write boundary (catches `SecurityError`,
//     `QuotaExceededError`, malformed JSON, and wrong-version payloads)
//
// The hook that uses this cache (`useChatSnapshot`) decides what to do
// with the loaded payload. This module never deletes a still-valid
// cache — that's the hook's job when the user changes thread or signs
// out. We never write into a slot that holds a different (user, thread)
// pair: each call site is responsible for the key it writes under.

import type { Database } from "@/types/db";

export type ChatMessageRow = Database["public"]["Tables"]["chat_messages"]["Row"];
export type ChatMessageReactionRow = Database["public"]["Tables"]["message_reactions"]["Row"];

/**
 * Local mirror of the `chat_snapshot_if_changed` RPC payload. The
 * backend owns `src/types/db.ts`; we intentionally redeclare the
 * contract here so the generated DB types don't have to know about
 * every JSON-shaped RPC the frontend calls. Keys are exact: `hash` and
 * `changed` are always present; `messages` and `reactions` only appear
 * when `changed === true`.
 */
export type ChatSnapshotResponse =
  | { hash: string; changed: false }
  | {
      hash: string;
      changed: true;
      messages: ChatMessageRow[];
      reactions: ChatMessageReactionRow[];
    };

/** The on-disk payload we persist. Versioned so future schema
 *  changes are clean: the read path rejects mismatches without
 *  mutating the slot. */
export type ChatSnapshot = {
  v: 1;
  hash: string;
  messages: ChatMessageRow[];
  reactions: ChatMessageReactionRow[];
};

const STORAGE_PREFIX = "famchest:chat:v1:";
const SCHEMA_VERSION = 1 as const;

/**
 * Build the `localStorage` key for a (user, thread) pair. The key
 * shape is stable across reloads and across browser tabs: a sign-out
 * + sign-in as a different user lands in a different slot, and a
 * single user's two different threads are also isolated from each
 * other. Bumping the prefix (`v1` → `v2`) effectively wipes the
 * cache globally; bumping the payload's `v` field wipes per slot.
 */
export function chatCacheKey(userId: string, threadId: string): string {
  return `${STORAGE_PREFIX}${userId}:${threadId}`;
}

/** Try to read the cached snapshot for this (user, thread). Returns
 *  `null` for every "no usable cache" condition: storage unavailable
 *  (private mode / SSR), wrong schema version, malformed JSON, wrong
 *  shape, or a slot owned by another user/thread.
 *
 *  Critically: this function never throws AND never deletes the slot.
 *  If the payload is malformed, the caller can decide whether to
 *  clear it (we deliberately don't). */
export function loadCachedSnapshot(
  userId: string,
  threadId: string,
): ChatSnapshot | null {
  if (typeof window === "undefined") return null;
  let raw: string | null = null;
  try {
    raw = window.localStorage.getItem(chatCacheKey(userId, threadId));
  } catch {
    // SecurityError on a third-party-cookie-blocked origin or any
    // other getItem failure. Treat as no cache.
    return null;
  }
  if (raw === null) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    // Malformed JSON — leave the slot alone; the caller can replace
    // it with a valid snapshot on the next successful fetch. Clearing
    // here would race with a still-valid cache that another tab is
    // about to write.
    return null;
  }
  return validateSnapshot(parsed);
}

/**
 * Persist a snapshot for this (user, thread). The write is best-effort
 * and never throws: storage quota, private mode, and any other failure
 * path are swallowed. We deliberately do NOT remove the previous value
 * on write failure — keeping a stale-but-valid cache is strictly
 * better than wiping history. Returns `true` on success.
 */
export function saveCachedSnapshot(
  userId: string,
  threadId: string,
  snapshot: ChatSnapshot,
): boolean {
  if (typeof window === "undefined") return false;
  let payload: string;
  try {
    payload = JSON.stringify(snapshot);
  } catch {
    return false;
  }
  try {
    window.localStorage.setItem(chatCacheKey(userId, threadId), payload);
    return true;
  } catch {
    // QuotaExceededError, SecurityError, etc. The cache is best-
    // effort: if we can't write, the next render still works from
    // memory, the user just won't get persistence.
    return false;
  }
}
export function clearCachedSnapshot(userId: string, threadId: string): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(chatCacheKey(userId, threadId));
  } catch {
    // Cache eviction is best-effort for restricted storage contexts.
  }
}


/**
 * Build a snapshot shape from a "changed: true" RPC response. The
 * `p_known_hash` matches `payload.hash` when the cache is still
 * authoritative; the caller passes that hash into the next request
 * so an unchanged server state returns the cheap `{changed:false}`
 * envelope. We compute the next "known hash" from this response
 * here so the hook stays simple.
 */
export function snapshotFromResponse(
  response: ChatSnapshotResponse,
): ChatSnapshot | null {
  if (!response.changed) return null;
  return {
    v: SCHEMA_VERSION,
    hash: response.hash,
    messages: response.messages,
    reactions: response.reactions,
  };
}

// ---------------------------------------------------------------------------
// Internals.
// ---------------------------------------------------------------------------


function validateSnapshot(value: unknown): ChatSnapshot | null {
  if (!isObject(value)) return null;
  if (value.v !== SCHEMA_VERSION) return null;
  if (typeof value.hash !== "string" || value.hash.length === 0) return null;
  if (!isChatMessageRows(value.messages)) return null;
  if (!isChatReactionRows(value.reactions)) return null;
  return value as ChatSnapshot;
}

export function isChatMessageRows(value: unknown): value is ChatMessageRow[] {
  return Array.isArray(value) && value.every((row) => {
    if (!isObject(row)) return false;
    return typeof row.id === "string"
      && typeof row.thread_id === "string"
      && typeof row.sender_member_id === "string"
      && nullableString(row.body)
      && nullableString(row.image_url)
      && nullableString(row.mime_type)
      && nullableNumber(row.voice_seconds)
      && (row.status === "sending" || row.status === "delivered" || row.status === "read")
      && typeof row.created_at === "string";
  });
}

export function isChatReactionRows(value: unknown): value is ChatMessageReactionRow[] {
  return Array.isArray(value) && value.every((row) => {
    if (!isObject(row)) return false;
    return typeof row.message_id === "string"
      && typeof row.member_id === "string"
      && typeof row.emoji === "string"
      && typeof row.created_at === "string"
      && (row.thread_id === undefined || typeof row.thread_id === "string");
  });
}

function nullableString(value: unknown): boolean {
  return value === null || typeof value === "string";
}

function nullableNumber(value: unknown): boolean {
  return value === null || typeof value === "number";
}

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}
